import React from 'react';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Package, Download, Search, Zap } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface StaffAllScannedItemsHeaderProps {
  totalItems: number;
  filteredItems: number;
  onExport: () => void;
  onQuickAdd: () => void;
}

export const StaffAllScannedItemsHeader = ({ 
  totalItems, 
  filteredItems, 
  onExport,
  onQuickAdd
}: StaffAllScannedItemsHeaderProps) => {
  const navigate = useNavigate();

  const handleBack = () => {
    navigate('/staff-dashboard');
  };

  return (
    <div className="bg-card rounded-lg shadow-sm border p-3 md:p-4 mb-4 md:mb-6">
      <div className="flex flex-col gap-3 md:gap-4">
        {/* Header Row */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 md:gap-3 min-w-0 flex-1">
            <Button variant="outline" onClick={handleBack} size="sm" className="shrink-0">
              <ArrowLeft className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Back to Dashboard</span>
              <span className="sm:hidden ml-1">Back</span>
            </Button>
            <div className="min-w-0">
              <h1 className="text-lg md:text-xl lg:text-2xl font-bold text-foreground truncate">
                All Scanned Items
              </h1>
              <p className="text-xs md:text-sm text-muted-foreground truncate">
                Comprehensive view of all scanned products
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button 
              onClick={onQuickAdd}
              size="sm"
              className="shrink-0"
            >
              <Zap className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Quick Add</span>
            </Button>
            
            <Button 
              variant="outline" 
              onClick={onExport}
              size="sm"
              className="shrink-0"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline ml-1">Export CSV</span>
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        <div className="bg-muted/30 rounded-lg p-3 space-y-2">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <Package className="w-4 h-4 text-muted-foreground" />
              <span className="font-medium text-foreground">
                {filteredItems.toLocaleString()} of {totalItems.toLocaleString()} items
              </span>
            </div>
            <div className="flex items-center gap-2">
              <Search className="w-4 h-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                Showing {filteredItems === totalItems ? 'all' : 'filtered'} results
              </span>
            </div>
          </div>
          
          {filteredItems !== totalItems && (
            <div className="text-xs text-muted-foreground">
              Use filters below to refine your search across all orders
            </div>
          )}
        </div>
      </div>
    </div>
  );
};